import { useEffect } from "react";

import Modal from "components/ui/modal/Modal";
import { useFindIntegrationProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { PaywallContent } from "./PaywallContent";
import { FeatureType, PaywallImg, PaywallVideo } from "./paywallUtils";

import css from "./PaywallModal.module.scss";

export type PaywallModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  featureType: FeatureType;
  contextSource?: string;
  trackingData?: EventData;
  renderMedia?: boolean;
  video?: boolean;
};
function PaywallModal({
  open,
  onOpenChange,
  featureType,
  contextSource,
  trackingData,
  renderMedia = true,
  video = false,
}: PaywallModalProps) {
  const { logEvent } = useTracking();
  // The response, not the type: destructuring `data` under this name sent the whole object —
  // and it now carries the install's extra send-form fields.
  const { data: provider } = useFindIntegrationProvider();
  const providerType = provider?.providerType;

  // Using useEffect for opening and onOpenChange for closing since some parents
  // unmount this component immediately, rather than letting the animation play first.
  // useEffect is there in case this component re-renders (shouldn't happen but better safe)
  useEffect(() => {
    if (open) {
      logEvent(EventNames.paywallModalOpened, {
        "Context source": contextSource,
        "Data type": featureType,
        "Connected provider": providerType,
        ...trackingData,
      });
    }
  }, [open, logEvent, contextSource, featureType, providerType, trackingData]);

  const onOpenChangeWrapper = (open: boolean) => {
    if (!open) {
      logEvent(EventNames.paywallModalDismissed, {
        "Context source": contextSource,
        "Data type": featureType,
        "Connected provider": providerType,
        ...trackingData,
      });
    }
    onOpenChange(open);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChangeWrapper}
      maxWidth={video ? "900px" : "520px"}
      padding={0}
    >
      {renderMedia &&
        (video ? (
          <PaywallVideo featureType={featureType} />
        ) : (
          <PaywallImg context={"MODAL"} featureType={featureType} className={css.modalImgHeight} />
        ))}

      <PaywallContent featureType={featureType} trackingData={trackingData} />
    </Modal>
  );
}

export default PaywallModal;
